import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";

import useHostHubNonFsvChicagoListings from "./useHostHubNonFsvChicagoListings";

const CAMPAIGN_END_DATE = new Date("2026-11-26T00:00:00Z");
const SNOOZE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Controls visibility of the Vendor Recommendation banner on the Host Hub.
 * Dismissing snoozes the banner for a week (rather than hiding it for good)
 * so it can resurface; it stops showing entirely once CAMPAIGN_END_DATE has
 * passed, regardless of snooze state.
 */
const useVendorRecommendationBanner = () => {
  const ssoId = useSelector(selectProfileSSOid);
  const storageKey = ssoId
    ? `hostHub:vendorRecommendation:dismissedUntil:${ssoId}`
    : null;
  // hook that checks if the user has any listings in CHI that are NOT FSV
  const { isLoading: isListingsLoading, listings: nonFsvChicagoListings } =
    useHostHubNonFsvChicagoListings();

  const [dismissedUntil, setDismissedUntil] = useState<
    number | null | undefined
  >(undefined);

  // checking dismissed status
  useEffect(() => {
    if (!storageKey) return;
    try {
      const stored = window.localStorage.getItem(storageKey);
      setDismissedUntil(stored ? Number(stored) : null);
    } catch {
      setDismissedUntil(null);
    }
  }, [storageKey]);

  // for when the user dismisses the card - hide for 1 week
  const dismiss = () => {
    if (!storageKey) return;
    const dismissedUntilTimestamp = Date.now() + SNOOZE_DURATION_MS;
    try {
      window.localStorage.setItem(storageKey, String(dismissedUntilTimestamp));
    } catch {
      // Ignore: quota / private-mode failures shouldn't break the page.
    }
    setDismissedUntil(dismissedUntilTimestamp);
  };

  // checking for if the campaign has ended to hide/show
  const isCampaignActive = Date.now() < CAMPAIGN_END_DATE.getTime();
  const isSnoozed =
    typeof dismissedUntil === "number" && Date.now() < dismissedUntil;

  return {
    isVisible:
      Boolean(storageKey) &&
      isCampaignActive &&
      dismissedUntil !== undefined &&
      !isSnoozed &&
      !isListingsLoading &&
      nonFsvChicagoListings.length > 0,
    dismiss,
  };
};

export default useVendorRecommendationBanner;
